import { useState, useCallback, useEffect, useRef } from 'react';
import { markTourSeenService } from 'api';
import { useAppSelector } from 'state/hooks';
import { TOUR_STEPS } from 'components/product-tour/tour-steps';

// Derive from the steps array so the count can never drift out of sync with it
// (a hardcoded value previously ended the tour one step early, at "Suggestion").
const TOTAL_STEPS = TOUR_STEPS.length;

interface TourState {
  currentStep: number;
  completed: boolean;
  dismissed: boolean;
}

const DEFAULT_STATE: TourState = {
  currentStep: 0,
  completed: false,
  dismissed: false,
};

export function useTour() {
  // Progression is kept in-memory only. The single source of truth for whether
  // a given account has seen the tour is the backend `has_seen_tour` flag
  // (mirrored into Redux as `hasSeenTour`). Previously this lived in
  // localStorage under `precision_tour_state`, which leaked across accounts on
  // a shared browser: a new user could be denied the tour because a previous
  // account had completed it. The server flag is per-account, so this is now
  // tied to the actual user.
  const [state, setState] = useState<TourState>(DEFAULT_STATE);
  const [hasStarted, setHasStarted] = useState(false);
  const userId = useAppSelector((s) => s.user.id);
  const hasSeenTour = useAppSelector((s) => s.user.hasSeenTour);
  const completedProfile = useAppSelector((s) => s.user.completedProfile);
  const completedBusiness = useAppSelector((s) => s.user.completedBusiness);

  // Reset all in-memory progress when the signed-in account changes (e.g. a
  // logout → login in the same tab without a reload). Keeps the tour strictly
  // per-account — no progress or "started" latch leaks from a previous user.
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId;
      setState(DEFAULT_STATE);
      setHasStarted(false);
    }
  }, [userId]);

  // The tour must only appear once onboarding is fully complete, so it never
  // overlaps with (or precedes) the onboarding modal.
  const onboardingComplete = completedProfile && completedBusiness;

  const isAuthenticated = !!localStorage.getItem('precisionAccess');

  // Eligible to *begin* the tour: a per-account, server-driven gate.
  const canStart =
    isAuthenticated &&
    onboardingComplete &&
    !hasSeenTour &&
    !state.completed &&
    !state.dismissed;

  // Latch activation for the session. The tour spans four pages and the user
  // navigates between them; ProtectedRoute refetches the user on every page
  // mount, and `markSeen` fires on first display — so `hasSeenTour` can flip to
  // true mid-tour. Without latching, that would deactivate the tour partway
  // through. Once started we keep it active across pages until it is completed
  // or skipped (and it resets on account change above).
  useEffect(() => {
    if (canStart && !hasStarted) setHasStarted(true);
  }, [canStart, hasStarted]);

  const isActive = hasStarted && !state.completed && !state.dismissed;

  // True once the tour will no longer be shown for this account — used by
  // downstream flows that should only run "after the tour". It's finished if it
  // was completed/skipped this session, or the server already recorded it as
  // seen for a user who never started it this session (a returning user). It is
  // deliberately NOT true merely because `hasSeenTour` flipped mid-tour (that
  // happens on first display via markSeen), so an in-progress tour still counts
  // as unfinished.
  const hasFinished =
    state.completed || state.dismissed || (hasSeenTour && !hasStarted);

  const markSeenOnServer = useCallback(() => {
    markTourSeenService().catch(() => {});
  }, []);

  const advance = useCallback(() => {
    setState(prev => {
      const next =
        prev.currentStep + 1 >= TOTAL_STEPS
          ? { ...prev, completed: true }
          : { ...prev, currentStep: prev.currentStep + 1 };
      if (next.completed) markSeenOnServer();
      return next;
    });
  }, [markSeenOnServer]);

  const dismiss = useCallback(() => {
    setState(prev => ({ ...prev, dismissed: true }));
    markSeenOnServer();
  }, [markSeenOnServer]);

  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  return {
    isActive,
    hasFinished,
    currentStep: state.currentStep,
    advance,
    dismiss,
    reset,
    markSeen: markSeenOnServer,
  };
}
